import EventEmitter from "eventemitter3";
import * as PIXI from "pixi.js";
import gsap from "gsap";
import MyApplication from "../../../pixi/MyApplication";
import AssetsManager from "../../../pixi/AssetsManager";
import BtnView from "./BtnView";
import PandaView, { PANDA_LABEL } from "../game/PandaView";
import DataManager from "../../../data/DataManager";
import MathUtil from "../../../util/MathUtil";

/**
 * TopViewクラス
 */
class TopView extends PIXI.Container {
  // ---------------------------------------------------------------------------------------------------------
  // --	VARS
  // ---------------------------------------------------------------------------------------------------------

  //アプリケーション
  private _app: PIXI.Application;

  //コンテナ
  private _container: PIXI.Container;

  //タイトル
  private _title: PIXI.Sprite;

  //パンダ
  private _panda: PandaView;

  //ボタン
  private _startBtn: PIXI.Sprite;
  private _rankingBtn: PIXI.Sprite;

  //イベントエミッター
  private _emitter: EventEmitter = new EventEmitter();
  public get emitter(): EventEmitter {
    return this._emitter;
  }

  // ---------------------------------------------------------------------------------------------------------
  // --	TopView
  // ---------------------------------------------------------------------------------------------------------

  /**
   * コンストラクタ
   */
  constructor() {
    super();

    //app参照
    this._app = MyApplication.app;

    //重ね順
    this.zIndex = 10;

    //コンテナ
    this._container = new PIXI.Container();
    this.addChild(this._container);

    //タイトル配置
    this._title = AssetsManager.instance.createSpriteByName("title.png");
    this._title.anchor.set(0.5);
    this._title.scale.set(3.5);
    this._title.x = MyApplication.screenWidth / 2;
    this._title.y = 260;
    this._container.addChild(this._title);

    //スタートボタン
    this._startBtn = AssetsManager.instance.createSpriteByName("btn_play.png");
    this._startBtn.scale.set(4);
    this._startBtn.x = 64 * 2;
    this._startBtn.y = 340 * 2;
    this._container.addChild(this._startBtn);
    this._startBtn.on("mousedown", (e) => {
      this._emitter.emit("START");
    });
    this._startBtn.on("touchstart", (e) => {
      this._emitter.emit("START");
    });

    //ランキングボタン
    this._rankingBtn = AssetsManager.instance.createSpriteByName("btn_lb.png");
    this._rankingBtn.scale.set(4);
    this._rankingBtn.x = 114 * 2;
    this._rankingBtn.y = 417 * 2;
    this._container.addChild(this._rankingBtn);
    this._rankingBtn.on("mousedown", (e) => {
      this._emitter.emit("RANKING");
    });
    this._rankingBtn.on("touchstart", (e) => {
      this._emitter.emit("RANKING");
    });

    //パンダ
    this._panda = new PandaView(false);
    this._container.addChild(this._panda);

    //リセット
    this.reset();
  }

  /**
   * リセット
   */
  reset() {
    //パンダ初期化
    this._panda.reset();
    this._panda.x = -this._panda.width;
    this._panda.y = 250 + Math.random() * 30;
    this._panda.scale.x = 1;

    //位置
    this.y = -this._app.renderer.height;
  }

  // --------------------------------------------------------------------------------------------------------------------------------------
  // --	 開く/閉じる
  // --------------------------------------------------------------------------------------------------------------------------------------

  /**
   * 開く
   */
  open() {
    gsap.to(this, {
      duration: 1,
      y: 0,
      ease: "power2.inOut",
      onComplete: this.opened.bind(this),
    });
  }

  opened(): void {
    //BGM
    AssetsManager.instance.playSound("sound_top", { loop: true, volume: 0.1 });

    //ボタンをアクティブに
    this._startBtn.cursor = "pointer";
    this._startBtn.interactive = true;
    this._rankingBtn.cursor = "pointer";
    this._rankingBtn.interactive = true;

    //パンダのアニメーション開始
    this._panda.gotoAndPlay(PANDA_LABEL.LABEL_JET, true, 4);
    this._panda.powerUp();
    this.leftToRight();
  }

  /**
   * 閉じる
   */
  close() {
    //BGMとめる
    AssetsManager.instance.stopSound("sound_top");

    //ボタを非アクティブにする
    this._startBtn.cursor = "none";
    this._startBtn.interactive = false;
    this._rankingBtn.cursor = "none";
    this._rankingBtn.interactive = false;

    //アニメーション
    gsap.killTweensOf(this);
    gsap.to(this, {
      duration: 1,
      ease: "power2.inOut",
      y: -this._app.renderer.height,
      onComplete: this.reset.bind(this),
    });
  }

  // --------------------------------------------------------------------------------------------------------------------------------------
  // --	 パンダの動き
  // --------------------------------------------------------------------------------------------------------------------------------------

  /**
   * パンダ　左--->右
   */
  private leftToRight(): void {
    this._panda.x = -100;
    this._panda.y = this.getRandomY();
    this._panda.scale.x = 1.0;
    gsap.killTweensOf(this._panda);
    gsap.to(this._panda, {
      ease: "none",
      duration: 90 / 60,
      delay: 15 / 60,
      x: this._app.renderer.width + 100,
      y: this.getRandomY(),
      onComplete: this.rightToLeft.bind(this),
    });
  }

  /**
   * パンダ　右--->左
   */
  private rightToLeft(): void {
    this._panda.x = this._app.renderer.width + 100;
    this._panda.y = this.getRandomY();
    this._panda.scale.x = -1.0;
    gsap.killTweensOf(this._panda);
    gsap.to(this._panda, {
      ease: "none",
      duration: 90 / 60,
      delay: 15 / 60,
      x: -100,
      y: this.getRandomY(),
      onComplete: this.leftToRight.bind(this),
    });
  }

  // --------------------------------------------------------------------------------------------------------------------------------------
  // --	 UTIL
  // --------------------------------------------------------------------------------------------------------------------------------------

  getRandomY(): number {
    return (250 + Math.random() * 110) * 2;
  }

  // --------------------------------------------------------------------------------------------------------------------------------------
  // --	 リサイズ
  // --------------------------------------------------------------------------------------------------------------------------------------

  resize() {
    const config: any = DataManager.instance.getData("config");
    this._container.y = (this._app.renderer.height - config.STAGE_HEIGHT) / 2;
  }

  // ---------------------------------------------------------------------------------------------------------
}

export default TopView;
